'use client';

import { RouteAccessParams } from '@/@studio/utils';
import { useEffectiveOrgUiContext } from '@/@subsidiary/hooks/useEffectiveOrgUiContext';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { useSession } from '@/providers/enhanced-session';
import { useMemo } from 'react';
import { AccessContext } from '../access/resolve-access';

/**
 * Assembles the viewer half of an access decision — the only place these
 * inputs are put together.
 *
 * Both the left nav and the Home screen's stage tiles consume this, so neither
 * can accidentally build the check from a different set of session/org fields
 * than the other. `isInternalForTiles` (not the raw session flag) is
 * deliberate: it goes false while "View as" is simulating a subsidiary, so an
 * internal user previewing a customer's org sees that customer's gating.
 */
export function useAccessContext(): AccessContext {
    const { data: session } = useSession();
    const effectiveOrg = useEffectiveOrgUiContext();
    const { hasFlag } = useFeatureFlags();

    return useMemo(() => {
        const routeAccessParams: RouteAccessParams = {
            user: {
                isInternal: effectiveOrg.isInternalForTiles,
                hasStudioAIAccess: session?.user?.hasStudioAIAccess,
                isUnaffiliated: session?.user?.isUnaffiliated,
            },
            org: effectiveOrg,
        };

        return {
            isInternal: effectiveOrg.isInternalForTiles,
            daniVisible:
                session?.user?.hasStudioAIAccess === true ||
                effectiveOrg.hasClientStudioAIAccess === true,
            hasFlag,
            routeAccessParams,
            orgName: effectiveOrg.orgName,
        };
    }, [session, effectiveOrg, hasFlag]);
}
